import * as React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { DateFieldProps, useDateFieldController } from './date-field.controller';
import { Flex } from '@common/components/Flex';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { FieldContainer } from '../field-container';
import { Hint } from '@common/components/hint';
import { useFieldContentStyle } from '../content-style.utils';
import { MessageBox } from '@common/components/MessageBox';
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable, useForceDisabled } from '../../context';

import './date-field.styles.scss';

export const DateField: React.FC<DateFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const { 
    datePickerValue,
    handleDatePickerChange,
    handleRawInputChange,
    handleDateBlur,
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    messageProps,
    closeMessage,
  } = useDateFieldController(props);

  const isEditable = useDatasheetResultFieldEditable(datasheetElement.original.editable !== false);
  const forceDisabled = useForceDisabled();
  const { formControlStyle } = useFieldContentStyle(datasheetElement.original);
  const [localValue, setLocalValue] = React.useState('');

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const fieldContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(datasheetElement.id, {
      focus: () => {
        const input = fieldContainerRef.current?.querySelector<HTMLInputElement>('input');
        input?.focus();
      },
    });
    return () => { registerField(datasheetElement.id, null); };
  }, [datasheetElement.id, registerField, isEditable]);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext} 
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <>
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          ref={fieldContainerRef}
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
        >
          <div style={{ width: '100%', height: '100%' }}>
            <DatePicker
              selected={forceDisabled ? null : datePickerValue}
              onChangeRaw={(e) => {
                if (isEditable && e) {
                  const value = (e.target as HTMLInputElement)?.value || '';
                  setLocalValue(value);
                }
              }}
              onSelect={(date) => {
                if (!date || !isEditable) return;

                const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`; // M/D/YYYY
                handleDateBlur(formatted);
                setLocalValue(formatted);
              }}
              disabled={!isEditable}
              readOnly={!isEditable}
              dateFormat="MM/dd/yyyy"
              placeholderText="MM/DD/YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={50}
              scrollableYearDropdown
              customInput={<input style={formControlStyle} />}
              onKeyDown={(e) => {
                if (!isEditable) return;
                if (e.key === 'Tab') {
                  const value = (e.target as HTMLInputElement)?.value || '';
                  handleDateBlur(value);
                  e.preventDefault();
                  if (e.shiftKey) {
                    focusPrev(datasheetElement.id);
                  } else {
                    focusNext(datasheetElement.id);
                  }
                  return;
                }
                if (e.key === 'Enter') {
                  const value = (e.target as HTMLInputElement)?.value || '';
                  // Trigger validation on Enter using the current input value
                  // handleDateBlur will decide whether to persist or show an error
                  handleDateBlur(value);
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onBlur={() => {
                if (!isEditable) return;
                handleDateBlur(localValue);
              }}
            />
          </div>
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
      </DatasheetItem>
    </>
  );
});
